<template>
  <div class="flex items-center justify-center py-16">
    <div class="w-full sm:w-auto sm:max-w-xl">
      <div
        class="flex items-center justify-between mx-4 border-b border-active-blue"
      >
        <div
          class="pr-24 text-sm font-bold cursor-pointer border-active-blue hover:opacity-100 hover:text-active"
          :class="
            isActiveTab('active-bookings')
              ? 'border-b-4 text-active'
              : 'text-green-type-1 opacity-25'
          "
          @click="setActiveTab('active-bookings')"
        >
          Active bookings
        </div>
        <div
          class="px-24 text-sm font-bold cursor-pointer border-active-blue hover:opacity-100 hover:text-active text-green-type-1"
          :class="
            isActiveTab('past-bookings')
              ? 'border-b-4 text-active'
              : 'text-green-type-1 opacity-25'
          "
          @click="setActiveTab('past-bookings')"
        >
          Past bookings
        </div>
      </div>
      <div class="px-4 mt-6">
        <template v-if="isLoading">
          <div class="px-4">
            <div v-for="i in 3" :key="i">
              <shimmer height="100px" width="100%" class="mb-4"></shimmer>
            </div>
          </div>
        </template>
        <template v-else>
          <template v-if="activeTab === 'active-bookings'">
            <template v-if="activeBookings.length > 0">
              <router-link
                :to="{
                  name: 'dashboard.view-booking',
                  params: { bookingId: booking.id },
                }"
                class="block px-4 py-5 cursor-pointer bg-gray-type-5"
                v-for="booking in activeBookings"
                :key="booking.id"
              >
                <div
                  class="flex justify-between w-full px-4 cursor-pointer lg:w-3/4"
                >
                  <div>
                    <div class="text-sm font-bold text-dark-type-3">
                      {{ booking.pickup.location }}
                    </div>
                  </div>
                  <div class="ml-6">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="12"
                    >
                      <g
                        fill="none"
                        fill-rule="evenodd"
                        transform="translate(-2 -4)"
                      >
                        <circle cx="10" cy="10" r="10" fill="none" />
                        <path
                          d="M3.333 10H15m-4.167-4.167L15 10l-4.167 4.166"
                          stroke="#737D78"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />
                      </g>
                    </svg>
                  </div>
                  <div class="ml-6">
                    <div class="text-sm font-bold text-dark-type-3">
                      {{ booking.destination.location }}
                    </div>
                  </div>
                </div>
                <div class="mt-4">
                  <div class="flex items-center px-2 py-2 bg-white" v-if="booking.route && booking.route.route_code">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Route Code:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      {{ booking.route.route_code }}
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Trip type:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      One way
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Trip day:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      {{ booking.start_date | date("D MMMM, YYYY") }}
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Status:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      <span
                        class="
                          inline-flex
                          items-center
                          px-2.5
                          py-0.5
                          rounded-full
                          text-xxs
                          font-medium
                        "
                        :class="{
                          'bg-yellow-100 text-yellow-800': booking.booking_status === 'pending',
                          'bg-green-100 text-green-800': booking.booking_status === 'active',
                        }"
                        >{{ booking.booking_status | titleCase }}</span
                      >
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white" v-if="booking.meta">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Mode of Payment:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      <span
                        class="
                          inline-flex
                          items-center
                          px-2.5
                          py-0.5
                          rounded-full
                          text-xxs
                          font-medium
                          bg-blue-100
                          text-blue-800
                        "
                        >{{ getPaymentMethod(booking) | titleCase }}</span
                      >
                    </div>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-else>
              <div
                class="flex flex-col items-center justify-center py-10 lg:py-40"
              >
                <p>
                  No active bookings found.
                  <a href="#" @click.prevent="fetchBookings" class="text-active"
                    >Click here</a
                  >
                  to refresh
                </p>
              </div>
            </template>
          </template>
          <template v-if="activeTab === 'past-bookings'">
            <template v-if="pastBookings.length > 0">
              <router-link
                :to="{
                  name: 'dashboard.view-booking',
                  params: { bookingId: booking.user_route_id },
                }"
                class="block px-4 py-5 cursor-pointer bg-gray-type-5"
                v-for="booking in pastBookings"
                :key="booking.id"
              >
                <div
                  class="flex justify-between w-full px-4 cursor-pointer lg:w-3/4"
                >
                  <div>
                    <div class="text-sm font-bold text-dark-type-3">
                      {{ booking.pickup.location }}
                    </div>
                  </div>
                  <div class="ml-6">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="12"
                    >
                      <g
                        fill="none"
                        fill-rule="evenodd"
                        transform="translate(-2 -4)"
                      >
                        <circle cx="10" cy="10" r="10" fill="none" />
                        <path
                          d="M3.333 10H15m-4.167-4.167L15 10l-4.167 4.166"
                          stroke="#737D78"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                        />
                      </g>
                    </svg>
                  </div>
                  <div class="ml-6">
                    <div class="text-sm font-bold text-dark-type-3">
                      {{ booking.destination.location }}
                    </div>
                  </div>
                </div>
                <div class="mt-4">
                  <div class="flex items-center px-2 py-2 bg-white" v-if="booking.route && booking.route.route_code">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Route Code:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      {{ booking.route.route_code }}
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Trip type:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      One way
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Trip day:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      {{ booking.trip_date | date("D MMMM, YYYY") }}
                    </div>
                  </div>
                  <div class="flex items-center px-2 py-2 mt-1 bg-white" v-if="booking.meta">
                    <div class="font-medium text-dark-type-4 text-xxs">
                      Mode of Payment:
                    </div>
                    <div class="ml-2 font-medium text-dark-type-3 text-xxs">
                      <span
                        class="
                          inline-flex
                          items-center
                          px-2.5
                          py-0.5
                          rounded-full
                          text-xxs
                          font-medium
                          bg-blue-100
                          text-blue-800
                        "
                        >{{ JSON.parse(booking.meta).payment_option | titleCase }}</span
                      >
                    </div>
                  </div>
                </div>
              </router-link>
            </template>
            <template v-else>
              <div
                class="flex flex-col items-center justify-center py-10 lg:py-40"
              >
                <p>
                  No past bookings found.
                  <a href="#" @click.prevent="fetchBookings" class="text-active"
                    >Click here</a
                  >
                  to refresh
                </p>
              </div>
            </template>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Shimmer from "@/components/Shimmer";

export default {
  components: {
    Shimmer,
  },
  data() {
    return {
      activeTab: "active-bookings",
      isLoading: true,
    };
  },
  created() {
    this.fetchBookings();
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("app", ["activeBookings", "pastBookings"]),
  },
  methods: {
    fetchBookings() {
      this.isLoading = true;

      Promise.all([
        this.fetchPastBookings(),
        this.fetchActiveBookings(),
      ]).finally(() => {
        this.isLoading = false;
      });
    },
    fetchActiveBookings() {
      return this.$store.dispatch("app/fetchActiveBookings");
    },
    handlePaymentSuccess(bookings) {
      this.$router.push({
        name: "dashboard.booking-successful",
        params: { bookingId: bookings[0].id },
      });
    },
    fetchPastBookings() {
      return this.$store.dispatch("app/fetchPastBookings");
    },
    getPaymentMethod(bookingData) {
      const schedule = bookingData.routeSchedules && bookingData.routeSchedules[0] ? bookingData.routeSchedules[0] : null;
      return schedule && schedule.payment_source ? schedule.payment_source : '';
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    isActiveTab(tab) {
      return this.activeTab === tab;
    },
  },
};
</script>